import { FC, useMemo, useState } from "react"
import { Alert, Box, Button, Chip, Switch, Typography } from "@mui/material"
import { Add, Edit, DeleteOutlined, Visibility } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { TooltipAction } from "../common/tooltipAction"
import { DataGrid, Modal } from "@/core/components"
import { useUrlListState } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import {
  useTriggersQuery,
  useDeleteTriggerMutation,
  useUpdateTriggerMutation,
} from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { isMatchAll, summarizeCondition } from "../fields/conditionExpr"
import { IdentityResolverCell } from "../identity"
import { TriggerForm } from "./triggerForm"
import { TriggerDetails } from "./triggerDetails"
import {
  isLegacyJoinTrigger,
  triggerReasonLabel,
  trafficPolicyErrorMessage,
} from "../common/trafficPolicy"

const formatNullableDate = (iso: string | null): string => {
  if (!iso) return "—"
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

const MatchCell: FC<{ row: Userbot.Trigger }> = ({ row }) => {
  if (isMatchAll(row.match)) {
    return <Chip size="small" label="все" />
  }
  const summary = summarizeCondition(row.match)
  const truncated = summary.length > 40 ? summary.slice(0, 37) + "…" : summary
  return <Chip size="small" label={truncated} variant="outlined" />
}

export const TriggersTable: FC = () => {
  const list = useUrlListState()
  const { data, isFetching, isError, error } = useTriggersQuery(list.listArgs, {
    pollingInterval: 60000,
  })
  const canWrite = useUserbotWriteAccess()
  const [deleteTrigger, { isLoading: isDeleting }] = useDeleteTriggerMutation()
  const [updateTrigger] = useUpdateTriggerMutation()

  const [editing, setEditing] = useState<Userbot.Trigger | null>(null)
  const [creating, setCreating] = useState(false)
  const [details, setDetails] = useState<Userbot.Trigger | null>(null)
  const [toDelete, setToDelete] = useState<Userbot.Trigger | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)

  const columns: GridColDef<Userbot.Trigger>[] = useMemo(
    () => [
      { field: "type", headerName: "Тип", flex: 2, sortable: false },
      {
        field: "clientId",
        headerName: "Клиент",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <IdentityResolverCell clientId={row.clientId} />
        ),
      },
      {
        field: "match",
        headerName: "Match",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => <MatchCell row={row} />,
      },
      {
        field: "inviteHash",
        headerName: "Invite",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => (value ? String(value) : "—"),
      },
      {
        field: "fireCount",
        headerName: "Срабатываний",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => `${row.fireCount}/${row.maxFires ?? "∞"}`,
      },
      {
        field: "lastFiredAt",
        headerName: "Последнее",
        flex: 1,
        renderCell: ({ value }) => formatNullableDate(value),
      },
      {
        field: "expiresAt",
        headerName: "Истекает",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => formatNullableDate(value),
      },
      {
        field: "oneShot",
        headerName: "oneShot",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) =>
          value ? <Chip size="small" label="да" /> : "—",
      },
      {
        field: "nonExecutableReason",
        headerName: "Причина недоступности",
        flex: 3,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ py: 0.5, whiteSpace: "normal" }}>
            {triggerReasonLabel(row.nonExecutableReason)}
          </Typography>
        ),
      },
      {
        field: "isActive",
        headerName: "Активен",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Switch
            checked={row.isActive}
            disabled={
              !canWrite || (!row.isActive && isLegacyJoinTrigger(row.type))
            }
            onChange={async (e) => {
              const nextActive = e.target.checked
              if (!canWrite || (nextActive && isLegacyJoinTrigger(row.type)))
                return
              setMutationError(null)
              try {
                await updateTrigger({
                  id: row.id,
                  body: { isActive: nextActive },
                }).unwrap()
              } catch (err) {
                setMutationError(trafficPolicyErrorMessage(err))
              }
            }}
          />
        ),
      },
    ],
    [canWrite, updateTrigger],
  )

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button startIcon={<Add />} onClick={() => setCreating(true)}>
            Создать триггер
          </Button>
        </Box>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}
      {mutationError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {mutationError}
        </Alert>
      )}
      <Box height="80vh">
        <DataGrid<Userbot.Trigger>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={[
            (r) => (
              <TooltipAction
                label="Детали"
                icon={<Visibility />}
                onClick={() => setDetails(r.row)}
              />
            ),
            ...(canWrite
              ? [
                  (r: { row: Userbot.Trigger }) => (
                    <TooltipAction
                      label="Редактировать"
                      icon={<Edit />}
                      onClick={() => setEditing(r.row)}
                    />
                  ),
                  (r: { row: Userbot.Trigger }) => (
                    <TooltipAction
                      label="Удалить"
                      icon={<DeleteOutlined />}
                      onClick={() => setToDelete(r.row)}
                    />
                  ),
                ]
              : []),
          ]}
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новый триггер"
          onClose={() => setCreating(false)}
          dialogProps={{ maxWidth: "lg" }}
          element={<TriggerForm onDone={() => setCreating(false)} />}
        />
      )}
      {editing && (
        <Modal
          disableBackdropClose
          title={`Триггер: ${editing.type}`}
          onClose={() => setEditing(null)}
          dialogProps={{ maxWidth: "lg" }}
          element={
            <TriggerForm trigger={editing} onDone={() => setEditing(null)} />
          }
        />
      )}
      {details && (
        <TriggerDetails trigger={details} onClose={() => setDetails(null)} />
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Удалить триггер?"
        description={toDelete ? `Триггер «${toDelete.type}» будет удалён.` : ""}
        loading={isDeleting}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteTrigger({ id: toDelete.id })
            setToDelete(null)
          }
        }}
      />
    </>
  )
}
